import { getComposeViewState } from 'owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState';
import {
    type Suggestion,
    type Suggestions,
    composeNewHelpWrite,
    composeNewFindRecipients,
    composeNewSupportStatement,
    composeReplyHelpReply,
    composeReplyNextSteps,
    composeReplyKeyPoints,
    composeForwardExplain,
    composeForwardWithContext,
    composeForwardWithTasks,
} from './suggestionCatalog';
import { getCachedSuggestedDrafts } from 'owa-copilot-suggested-drafts/lib/cache/suggestedDraftsCache';
import { executePromptWithQueryAnnotation } from './executePromptWithQueryAnnotation';
import loc from 'owa-localize';
import { isFeatureEnabled } from 'owa-feature-flags';
import { type ComposeOperation } from 'owa-mail-compose-store/lib/store/schema/ComposeOperation';
import { replyPromptText, draftReply } from './getComposingSuggestions.locstring.json';

export function getComposingSuggestions(): Suggestions {
    const composeViewState = getComposeViewState();

    if (!composeViewState) {
        return undefined;
    }

    // Use suggestions from experiments if available
    if (isFeatureEnabled('mon-copilot-dab-experiment-draft-reply')) {
        const draftSuggestions = getDraftReplySuggestions(composeViewState);
        if (draftSuggestions) {
            return draftSuggestions;
        }
    }

    // Default SUAs
    if (isFeatureEnabled('copilot-dab-mail-compose-sua')) {
        return getComposeScenarioSuggestions(composeViewState.operation);
    }

    return undefined;
}

function getDraftReplySuggestions(composeViewState: {
    conversationId?: string;
    referenceItemId?: {
        Id?: string;
    } | null;
}): Suggestions {
    const { conversationId, referenceItemId } = composeViewState;

    const [cachedSuggestedDrafts] = getCachedSuggestedDrafts(
        referenceItemId?.Id ?? '',
        conversationId ?? '',
        'Polaris',
        () => {},
        'Outlook'
    );

    const suggestedDraft1Text = cachedSuggestedDrafts[0]?.text;
    const suggestedDraft2Text = cachedSuggestedDrafts[1]?.text;
    const suggestedDraft3Text = cachedSuggestedDrafts[2]?.text;

    const hasSuggestedDrafts =
        cachedSuggestedDrafts.length >= 3 &&
        suggestedDraft1Text &&
        suggestedDraft2Text &&
        suggestedDraft3Text;

    if (!hasSuggestedDrafts) {
        return undefined;
    }

    // For composing scenario, we prompts for suggested drafts if they were generated
    return [
        getDraftSuggestion('suggestedDraft1', suggestedDraft1Text),
        getDraftSuggestion('suggestedDraft2', suggestedDraft2Text),
        getDraftSuggestion('suggestedDraft3', suggestedDraft3Text),
    ];
}

const getDraftSuggestion = (id: string, text: string): Suggestion => {
    return {
        displayTextLocalized: loc(draftReply, text),
        id,
        onClick: (targetWindow: Window) =>
            executePromptWithQueryAnnotation(
                loc(replyPromptText, text),
                'cmm9uaofbq8',
                targetWindow
            ),
    };
};

function getComposeScenarioSuggestions(operation?: ComposeOperation): Suggestions {
    switch (operation) {
        case 3:
            return [composeForwardExplain, composeForwardWithContext, composeForwardWithTasks];
        case 1:
        case 2:
            return [composeReplyHelpReply, composeReplyNextSteps, composeReplyKeyPoints];
        default:
            return [composeNewHelpWrite, composeNewFindRecipients, composeNewSupportStatement];
    }
}
